import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { getRevenueChartDataAction } from "../../Actions/TransactionActions";
import NoDataFound from "./NoDataFound";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";
import {Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const DashboardRevenueGraph = ({
  selectedMerchant,
  selectedWebsite,
  selectedTimeRange,
  startDate,
  endDate,
  showButton,
  onRemove,
  onShowSnackbar,
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Revenue",
        data: [],
        backgroundColor: "#344D95",
        borderRadius: 6,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        barThickness: 30,
      },
    ],
  });
  const [loading, setLoading] = useState(false);

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getRevenueChartDataAction({
        startDate: startDate,
        endDate: endDate,
        merchant: selectedMerchant,
        chartType: selectedTimeRange,
        websiteId: selectedWebsite || null,
      });

      if (response?.data?.labels && response?.data?.data) {
        setChartData({
          labels: response.data.labels,
          datasets: [
            {
              label: "Revenue",
              data: response.data.data,
              backgroundColor: "#344D95",
              borderRadius: 6,
              barPercentage: 0.8,
              categoryPercentage: 0.9,
              barThickness: 30,
            },
          ],
        });
      } else {
        setChartData({
          labels: [],
          datasets: [
            {
              label: "Revenue",
              data: [],
              backgroundColor: "#344D95",
              borderRadius: 6,
              barPercentage: 0.8,
              categoryPercentage: 0.9,
              barThickness: 30,
            },
          ],
        });
      }
    } catch (error) {
      setChartData({
        labels: [],
        datasets: [
          {
            label: "Revenue",
            data: [],
            backgroundColor: "#344D95",
            borderRadius: 6,
            barPercentage: 0.8,
            categoryPercentage: 0.9,
            barThickness: 30,
          },
        ],
      });
      if (error?.response?.data?.message) {
        onShowSnackbar(error.response.data.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, selectedTimeRange, onShowSnackbar]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchChartData();
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, fetchChartData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Revenue by Tenant" },
    },
    scales: {
      x: { title: { display: true, text: "Tenant" } },
      y: {
        title: { display: true, text: "Revenue ($)" },
        beginAtZero: true,
      },
    },
  };


  return (
    <div className={`kpi-card ${!showButton && "my-3"}`} key="revenueMerchantChart">
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("revenueMerchantChart")}
        />
      )}

      <div className="kpi-card-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="chart-title m-0">Revenue by Tenant</h2>
        </div>

        <div className="chart-canvas-container" style={{ height: "350px" }}>
          {loading ? (
            <div className="chart-loading">
              <CircularProgress />
              <p>Loading revenue data...</p>
            </div>
          ) : chartData.datasets[0].data.length === 0 || chartData.datasets[0].data.every((value) => value === 0) ? (
            <NoDataFound message="No revenue data available" />
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardRevenueGraph;